import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Control, FieldValues, useForm } from 'react-hook-form'

import { useAnnouncement } from '../../../providers/Announcement/Announcement.provider'
import { SendAnnouncementRequestData } from '../../../providers/Announcement/types'
import { useOptions } from '../../../providers/Options/Options.provider'
import { Options } from '../../../providers/Options/types'
import { PeerGroupListItem } from '../../../providers/PeerGroups/types'
import { Provider } from '../../../providers/ProvidersProvider/types'
import {
  getFieldValidationSchema,
  getInputFieldBasedOnType,
} from '../../../utilities/Forms/SectionFields'
import { Field, Section, SectionFields } from '../../../utilities/Forms/types'
import { ANNOUNCEMENT_GROUP_FIELDS } from './AnnouncementFields'
import { AnnouncementsView } from './Announcements.view'

export type SendToOption =
  | 'all_patients'
  | 'all_my_patients'
  | 'provider_patients'
  | 'peer_group'

export type ScheduleMode = 'immediate' | 'scheduled'

const Announcements = () => {
  const {
    sendNewAnnouncement,
    sendAnnouncementToAllOfMyPatients,
    sendAnnouncementToProvidersPatients,
    sendAnnouncementToPeerGroupParticipants,
    scheduleAnnouncement,
    scheduleAnnouncementToAllOfMyPatients,
    scheduleAnnouncementToProvidersPatients,
    scheduleAnnouncementToPeerGroupParticipants,
    announcements,
  } = useAnnouncement()
  const { options } = useOptions()
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(getFieldValidationSchema(ANNOUNCEMENT_GROUP_FIELDS)),
  })

  const [profileDetailFields, setProfileDetailFields] = useState<SectionFields>(
    ANNOUNCEMENT_GROUP_FIELDS
  )
  const [announcementCreated, setAnnouncementCreated] = useState<boolean>(true)
  const [announcementSubmitting, setAnnouncementSubmitting] =
    useState<boolean>(false)

  const [sendToOption, setSendToOption] = useState<SendToOption>('all_patients')
  // State for send to dropdown
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  )
  const [selectedPeerGroup, setSelectedPeerGroup] =
    useState<PeerGroupListItem | null>(null)

  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('immediate')
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('')

  const onSubmit = async (values: FieldValues) => {
    let Announcement: SendAnnouncementRequestData = {
      name: '',
      description: '',
    }

    if (scheduleMode === 'scheduled') {
      if (scheduledDateTime) {
        setAnnouncementSubmitting(true)
      } else {
        alert('Please select a delivery time.')
      }
    } else {
      setAnnouncementSubmitting(true)
    }

    switch (sendToOption) {
      case 'all_patients':
        Announcement = {
          name: values.topicName,
          description: values.description,
        }
        break
      case 'all_my_patients':
        Announcement = {
          name: values.topicName,
          description: values.description,
        }
        break
      case 'provider_patients':
        Announcement = {
          name: values.topicName,
          description: values.description,
          providerId: selectedProvider?.providerId,
        }
        break
      case 'peer_group':
        Announcement = {
          name: values.topicName,
          description: values.description,
          peerGroupId: selectedPeerGroup?.peerGroupId,
        }
        break
      default:
        Announcement = {
          name: values.topicName,
          description: values.description,
        }
    }

    if (scheduleMode === 'scheduled') {
      Announcement.sendAt = new Date(scheduledDateTime)
    }

    try {
      switch (sendToOption) {
        case 'all_patients':
          scheduleMode === 'scheduled'
            ? await scheduleAnnouncement(Announcement)
            : await sendNewAnnouncement(Announcement)
          break
        case 'all_my_patients':
          scheduleMode === 'scheduled'
            ? await scheduleAnnouncementToAllOfMyPatients(Announcement)
            : await sendAnnouncementToAllOfMyPatients(Announcement)
          break
        case 'provider_patients':
          scheduleMode === 'scheduled'
            ? await scheduleAnnouncementToProvidersPatients(Announcement)
            : await sendAnnouncementToProvidersPatients(Announcement)
          break
        case 'peer_group':
          scheduleMode === 'scheduled'
            ? await scheduleAnnouncementToPeerGroupParticipants(Announcement)
            : await sendAnnouncementToPeerGroupParticipants(Announcement)
          break
        default:
          await sendNewAnnouncement(Announcement)
      }
      setAnnouncementSubmitting(false)
      setAnnouncementCreated(false)
    } catch (err) {
      // console.error(err)
    }
  }

  const addInputFields = (
    detailFields: Field[],
    options: Options,
    errors: any,
    control: Control<FieldValues, any>
  ) =>
    detailFields.map((detailField) => ({
      ...detailField,
      editComponent: getInputFieldBasedOnType({
        field: detailField,
        options,
        errors,
        control,
      }),
    }))

  useEffect(() => {
    if (announcements) {
      const formVersionOfDetails = profileDetailFields.map((section) => ({
        ...section,
        fields: addInputFields(section.fields, options, errors, control),
      }))
      setProfileDetailFields(formVersionOfDetails)
    }
  }, [announcements, options, errors])

  useEffect(() => {
    profileDetailFields.forEach((section: Section) => {
      section.fields.forEach((field: Field) => {
        setValue(
          field.backendKey,
          announcements
            ? (announcements as any)[field.backendKey]
            : field.initialValue
        )
      })
    })
  }, [announcements, options])

  return announcements ? (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AnnouncementsView
          announcementDetailFields={profileDetailFields}
          user={announcements}
          {...{
            announcementCreated,
            setAnnouncementCreated,
            announcementSubmitting,
            setAnnouncementSubmitting,
            sendToOption,
            setSendToOption,
            selectedProvider,
            setSelectedProvider,
            selectedPeerGroup,
            setSelectedPeerGroup,
            scheduleMode,
            setScheduleMode,
            scheduledDateTime,
            setScheduledDateTime,
          }}
        />
      </form>
    </>
  ) : (
    <></>
  )
}

export { Announcements }
